// pages/Facturas.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  InputNumber,
  Divider,
  DatePicker,
  Checkbox,
  Radio,
  Descriptions,
  Collapse
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  DollarOutlined,
  OrderedListOutlined,
  SortAscendingOutlined,
  PrinterOutlined,
  DownloadOutlined,
  CalendarOutlined,
  UserOutlined,
  CreditCardOutlined,
  EuroOutlined,
  FilterOutlined,
  RightOutlined,
  DownOutlined,
  UnorderedListOutlined,
  CheckOutlined,
  WalletOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from '../utils/dayjsConfig';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const Facturas = () => {
  // Estados principales
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detallesExpandidos, setDetallesExpandidos] = useState({});
  const [pagosExpandidos, setPagosExpandidos] = useState({});
  
  // Estados para filtros
    const [filtros, setFiltros] = useState({
        fechaDesde: dayjs().subtract(7, 'days'),
        fechaHasta: dayjs(),
        ejercicio: null, // Se establecerá después de cargar ejercicios
        agencia: null,
        nfactura: '',
        snumero: '',
        contabilizada: null,
        cobrada: null
    });
    
    const [ejercicios, setEjercicios] = useState([]);    
  
  // Estados para datos auxiliares
  const [agencias, setAgencias] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  
  // Estados para modales
  const [modalCobroVisible, setModalCobroVisible] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [modalDetallesVisible, setModalDetallesVisible] = useState(false);
  const [facturaDetalles, setFacturaDetalles] = useState(null);
  const [detallesFactura, setDetallesFactura] = useState([]);
  const [pagosFactura, setPagosFactura] = useState([]);
  
  // Form para cobro
  const [formCobro] = Form.useForm();
  
  // Estados para selección múltiple
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  
  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'N° Factura',
      dataIndex: 'NFactura',
      key: 'NFactura',
      width: 120,
      sorter: (a, b) => a.NFactura - b.NFactura,
      render: (nfactura, record) => (
        <div>
          <Tag 
            color="blue" 
            style={{ 
              fontWeight: 'bold', 
              fontSize: '14px',
              padding: '4px 8px'
            }}
          >
            {record.Serie || 'A'}/{nfactura}
          </Tag>
          <div style={{ fontSize: '11px', color: '#666' }}>
            Ejercicio: {record.Ejercicio}
          </div>
        </div>
      ),
      fixed: 'left',
    },
    {
      title: 'Fecha',
      dataIndex: 'Fecha',
      key: 'Fecha',
      width: 120,
      render: (fecha) => (
        <div>
          <CalendarOutlined style={{ marginRight: 4, color: '#1890ff' }} />
          {fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'}
        </div>
      ),
    },
    {
      title: 'Agencia',
      dataIndex: 'NomAgencia',
      key: 'NomAgencia',
      width: 200,
      render: (nomAgencia, record) => (
        <div style={{ maxWidth: 200 }}>
          <Text strong style={{ fontSize: '13px' }}>
            {record.Agencia} - {nomAgencia || 'Sin agencia'}
          </Text>
          {record.NomSubAgen && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              Sub: {record.NomSubAgen}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Localizador',
      dataIndex: 'SNumero',
      key: 'SNumero',
      width: 150,
      render: (snumero) => (
        <Tag color="cyan" style={{ fontFamily: 'monospace' }}>
          {snumero || '-'}
        </Tag>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'Total',
      key: 'Total',
      width: 120,
      render: (total) => (
        <div style={{ textAlign: 'right' }}>
          <strong style={{ color: '#1890ff', fontSize: '14px' }}>
            {total ? `${parseFloat(total).toFixed(2)} €` : '0.00 €'}
          </strong>
        </div>
      ),
    },
    {
      title: 'Contabilizada',
      dataIndex: 'Contabilizado',
      key: 'Contabilizado',
      width: 120,
      render: (contabilizado) => (
        contabilizado === 'S' ? (
          <Badge
            status="success"
            text="Sí"
            style={{ color: '#52c41a', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="default"
            text="No"
            style={{ color: '#8c8c8c', fontWeight: 'bold' }}
          />
        )
      ),
    },
    {
      title: 'Cobrada',
      dataIndex: 'Cobrado',
      key: 'Cobrado',
      width: 120,
      render: (cobrado, record) => {
        const pagado = parseFloat(record.TPagado || 0);
        const total = parseFloat(record.Total || 0);
        const pendiente = total - pagado;
        
        if (cobrado === 'S' || pendiente <= 0) {
          return (
            <Badge
              status="success"
              text="Completo"
              style={{ color: '#52c41a', fontWeight: 'bold' }}
            />
          );
        } else if (pagado > 0) {
          return (
            <Badge
              status="processing"
              text={`Parcial (${pendiente.toFixed(2)}€)`}
              style={{ color: '#1890ff', fontWeight: 'bold' }}
            />
          );
        } else {
          return (
            <Badge
              status="error"
              text="Pendiente"
              style={{ color: '#ff4d4f', fontWeight: 'bold' }}
            />
          );
        }
      },
    },
    {
      title: 'Estado Pago',
      key: 'estadoPago',
      width: 150,
      render: (_, record) => {
        const pagado = parseFloat(record.TPagado || 0);
        const total = parseFloat(record.Total || 0);
        const pendiente = total - pagado;
        
        return (
          <div>
            <div style={{ fontSize: '12px' }}>
              <strong>Pagado:</strong> {pagado.toFixed(2)}€
            </div>
            <div style={{ fontSize: '12px' }}>
              <strong>Pendiente:</strong> {pendiente.toFixed(2)}€
            </div>
            {pendiente > 0 && (
              <Progress 
                percent={Math.round((pagado / total) * 100)} 
                size="small" 
                status="active"
              />
            )}
          </div>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleVerDetalles(record)}
            />
          </Tooltip>
          
          <Tooltip title="Cobrar">
            <Button
              type="primary"
              icon={<DollarOutlined />}
              size="small"
              onClick={() => handleCobrar(record)}
              disabled={record.Cobrado === 'S'}
            />
          </Tooltip>
          
          <Tooltip title="Imprimir">
            <Button
              type="default"
              icon={<PrinterOutlined />}
              size="small"
              onClick={() => handleImprimir(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Anular factura?"
            description="Esta acción anulará la factura permanentemente"
            onConfirm={() => handleAnular(record.NFactura)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Anular">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                disabled={record.Contabilizado === 'S'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

    // Cargar datos iniciales
    useEffect(() => {
        fetchEjercicios();
        fetchFacturas();
        fetchAgencias();
        fetchFormasPago();
    }, []);

    const fetchFacturas = async (filtrosAdicionales = {}) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
    
            // Combinar filtros
            const params = {
                ...filtros,
                ...filtrosAdicionales
            };
    
            // Formatear fechas
            if (params.fechaDesde) {
                params.fechaDesde = dayjs(params.fechaDesde).format('YYYY-MM-DD');
            }
            if (params.fechaHasta) {
                params.fechaHasta = dayjs(params.fechaHasta).format('YYYY-MM-DD');
            }
    
            // Asegurarse de que se envíe el ejercicio
            if (!params.ejercicio) {
                // Si no hay ejercicio seleccionado, usar el más reciente
                if (ejercicios.length > 0) {
                    params.ejercicio = ejercicios[0].value;
                }
            }
    
            // Limpiar valores vacíos
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) {
                    delete params[key];
                }
            });
    
            // Si no hay filtros esenciales, no buscar
            // if (!params.fechaDesde || !params.fechaHasta || !params.ejercicio) {
            if (!params.ejercicio) {
                setFacturas([]);
                setLoading(false);
                return;
            }
    
            const response = await axios.get(`${BASE_URL}/api/facturas`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            const facturasData = response.data.data || [];
    
            // Agregar campo expandible
            const facturasConDetalles = facturasData.map(factura => ({
                ...factura,
                key: factura.NFactura,
                expandible: true
            }));
    
            setFacturas(facturasConDetalles);
    
            message.success(`Se encontraron ${facturasConDetalles.length} facturas`);
    
        } catch (error) {
            console.error('❌ Error cargando facturas:', error);
            message.error('Error al cargar las facturas');
            setFacturas([]);
        } finally {
            setLoading(false);
        }
    };

  const fetchAgencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/agencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAgencias(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando agencias:', error);
      message.error('Error al cargar las agencias');
    }
  };

  const fetchFormasPago = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/formaspago`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormasPago(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando formas de pago:', error);
      message.error('Error al cargar las formas de pago');
    }
  };

  const fetchDetallesFactura = async (nfactura,ejercicio) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/facturas/${nfactura}/${ejercicio}/detalles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('❌ Error cargando detalles:', error);
      return [];
    }
  };

  const fetchPagosFactura = async (nfactura,ejercicio) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/facturas/${nfactura}/${ejercicio}/pagos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      return response.data.data || [];
    } catch (error) {
      console.error('❌ Error cargando pagos:', error);
      return [];
    }
  };

    // Agregar esta función en el useEffect inicial y en fetchAgencias
    const fetchEjercicios = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASE_URL}/api/facturas/ejercicios`, {
                headers: { Authorization: `Bearer ${token}` }
            });
        
            const ejerciciosData = response.data.data || [];
            setEjercicios(ejerciciosData);
        
            // Establecer el ejercicio más reciente por defecto
            if (ejerciciosData.length > 0) {
                const ejercicioMasReciente = ejerciciosData[0]; // Ya viene ordenado del más reciente
                setFiltros(prev => ({
                    ...prev,
                    ejercicio: ejercicioMasReciente.value
                }));
            }
        
        } catch (error) {
            console.error('❌ Error cargando ejercicios:', error);
            message.error('Error al cargar los ejercicios');
        }
    };


    const handleEliminarCobro = async (factura, pago) => {
        try {
            const token = localStorage.getItem('token');
        
            // Confirmación adicional para cobros grandes
            if (parseFloat(pago.Pagado) > 1000) {
                Modal.confirm({
                    title: 'Confirmar eliminación',
                    content: `¿Está seguro de eliminar este cobro de ${parseFloat(pago.Pagado).toFixed(2)} €?`,
                    okText: 'Sí, eliminar',
                    cancelText: 'Cancelar',
                    okButtonProps: { danger: true },
                    onOk: async () => {
                        await eliminarCobro(factura, pago, token);
                    }
                });
            } else {
                await eliminarCobro(factura, pago, token);
            }
        
        } catch (error) {
            console.error('❌ Error eliminando cobro:', error);
            message.error(error.response?.data?.error || 'Error al eliminar el cobro');
        }
    };

    // Función para eliminar cobro
    const eliminarCobro = async (factura, pago, token) => {
        console.log(" pago => ",pago);
        
        const response = await axios.delete(
            `${BASE_URL}/api/facturas/cobros/${pago.incre}`,
            { 
            headers: { Authorization: `Bearer ${token}` },
                params: {
                    nfactura: factura.NFactura,
                    serie: factura.Serie || 'A',
                    ejercicio: factura.Ejercicio
                }
            }
        );
  
        message.success('Cobro eliminado correctamente');
  
        // Recargar los pagos de esta factura
        const nuevosPagos = await fetchPagosFactura(factura.NFactura);
        setPagosExpandidos(prev => ({
            ...prev,
            [factura.NFactura]: nuevosPagos
        }));
  
        // Recargar la factura para actualizar estado
        fetchFacturas();
    };
  
    // Handlers para acciones
  const handleVerDetalles = async (factura) => {
    
    
    setModalDetallesVisible(true);
    setFacturaDetalles(factura);
    
    // Cargar detalles y pagos
    const detalles = await fetchDetallesFactura(factura.NFactura,factura.Ejercicio);
    const pagos = await fetchPagosFactura(factura.NFactura,factura.Ejercicio);
    
    setDetallesFactura(detalles);
    setPagosFactura(pagos);
  };

  const handleCobrar = (factura) => {
    setFacturaSeleccionada(factura);
    formCobro.setFieldsValue({
      fecha: dayjs(),
      formaPago: 'EFE',
      importe: parseFloat(factura.Total) - parseFloat(factura.TPagado || 0)
    });
    setModalCobroVisible(true);
  };

  const handleImprimir = (factura) => {
    Modal.info({
      title: 'Imprimir Factura',
      content: `¿Imprimir factura ${factura.Serie || 'A'}/${factura.NFactura}?`,
      okText: 'Imprimir',
      cancelText: 'Cancelar',
      onOk: () => {
        window.open(`${BASE_URL}/api/facturas/${factura.NFactura}/imprimir`, '_blank');
      }
    });
  };

  const handleAnular = async (nfactura) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/facturas/${nfactura}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Actualizar lista
      setFacturas(facturas.filter(f => f.NFactura !== nfactura));
      
      message.success('Factura anulada correctamente');
    } catch (error) {
      console.error('❌ Error anulando factura:', error);
      message.error(error.response?.data?.error || 'Error al anular la factura');
    }
  };

  const handleCobroSubmit = async () => {
    try {
      const values = await formCobro.validateFields();
      const token = localStorage.getItem('token');
      
      const cobroData = {
        ...values,
        nfactura: facturaSeleccionada.NFactura,
        serie: facturaSeleccionada.Serie || 'A',
        ejercicio: facturaSeleccionada.Ejercicio
      };
      
      await axios.post(`${BASE_URL}/api/facturas/cobrar`, cobroData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('Pago registrado correctamente');
      setModalCobroVisible(false);
      formCobro.resetFields();
      
      // Recargar facturas
      fetchFacturas();
      
    } catch (error) {
      console.error('❌ Error registrando pago:', error);
      message.error(error.response?.data?.error || 'Error al registrar el pago');
    }
  };

    const handleFiltrar = () => {
        // Validar que se tengan los filtros mínimos
        // if (!filtros.fechaDesde || !filtros.fechaHasta) {
        //     message.warning('Debe seleccionar un rango de fechas');
        //     return;
        // }
  
        if (!filtros.ejercicio) {
            message.warning('Debe seleccionar un ejercicio');
            return;
        }
  
        fetchFacturas();
    };

    const handleLimpiarFiltros = () => {
        const fechaHasta = dayjs();
        const fechaDesde = dayjs().subtract(7, 'days');
        const ejercicioDefault = ejercicios.length > 0 ? ejercicios[0].value : null;
  
        setFiltros({
            fechaDesde,
            fechaHasta,
            ejercicio: ejercicioDefault,
            agencia: null,
            nfactura: '',
            snumero: '',
            contabilizada: null,
            cobrada: null
        });
  
        // No cargar facturas automáticamente
        setFacturas([]);
    };

  const handleImprimirResumen = () => {
    if (selectedRows.length === 0) {
      message.warning('Seleccione al menos una factura para imprimir');
      return;
    }
    
    const ids = selectedRows.map(row => row.NFactura).join(',');
    window.open(`${BASE_URL}/api/facturas/imprimir-resumen?ids=${ids}`, '_blank');
  };

  const handleImprimirTodas = () => {
    if (facturas.length === 0) {
      message.warning('No hay facturas para imprimir');
      return;
    }
    
    const ids = facturas.map(f => f.NFactura).join(',');
    window.open(`${BASE_URL}/api/facturas/imprimir-todas?ids=${ids}`, '_blank');
  };

  const handleExportar = () => {
    message.info('Exportando facturas...');
    // Implementar exportación
  };

  // Configuración de tablas expandibles
  const expandedRowRender = (record) => {
    // console.log(' expandedRowRender ');
    
    const detalles = detallesExpandidos[record.NFactura] || [];
    const pagos = pagosExpandidos[record.NFactura] || [];

    // console.log(detalles);
    // console.log(pagos);
    
    
    
    return (
      <div style={{ margin: 0 }}>
        <Collapse defaultActiveKey={['detalles']}>
          <Panel header="Detalles de la Factura" key="detalles">
            <Table
              columns={[
                { title: 'Habitación', dataIndex: 'Habitacion', key: 'Habitacion', width: 100 },
                { title: 'Cliente', dataIndex: 'Cliente', key: 'Cliente', width: 200 },
                { title: 'Entrada', dataIndex: 'FechaEnt', key: 'FechaEnt', 
                  render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                { title: 'Salida', dataIndex: 'FechaSal', key: 'FechaSal',
                  render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                { title: 'Pax', dataIndex: 'Nagrupa', key: 'Nagrupa', width: 80 },
                { title: 'Días', dataIndex: 'DiasCant', key: 'DiasCant', width: 80 },
                { title: 'Precio', dataIndex: 'Precio', key: 'Precio', width: 100,
                  render: precio => `${parseFloat(precio || 0).toFixed(2)} €` },
                { title: 'Base', dataIndex: 'ImporteBase', key: 'ImporteBase', width: 100,
                  render: base => `${parseFloat(base || 0).toFixed(2)} €` },
                { title: 'IVA', dataIndex: 'ImporteIVA', key: 'ImporteIVA', width: 100,
                  render: iva => `${parseFloat(iva || 0).toFixed(2)} €` },
              ]}
              dataSource={detalles}
              rowKey="i"
              size="small"
              pagination={false}
            />
          </Panel>
          
          <Panel header="Pagos Registrados" key="pagos">
            <Table
              columns={[
                { title: 'Fecha', dataIndex: 'Fecha', key: 'Fecha',
                  render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                { title: 'Forma Pago', dataIndex: 'FPago', key: 'FPago', width: 120 },
                { title: 'Importe', dataIndex: 'Pagado', key: 'Pagado', width: 100,
                  render: importe => `${parseFloat(importe || 0).toFixed(2)} €` },
                { title: 'Usuario', dataIndex: 'usuario', key: 'usuario', width: 100 },
                { title: 'Contabilizado', dataIndex: 'Contabilizado', key: 'Contabilizado', width: 100,
                  render: contabilizado => contabilizado ? 'Sí' : 'No' },
                { title: 'Acciones', key: 'acciones', width: 100,
                    render: (_, pago) => (
                        <Tooltip title="Eliminar cobro">
                            <Popconfirm
                                title="¿Eliminar este cobro?"
                                description="Esta acción eliminará el registro de pago"
                                onConfirm={() => handleEliminarCobro(record, pago)}
                                okText="Sí"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    danger
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        disabled={pago.Contabilizado} // No permitir eliminar si ya está contabilizado
                                />    
                            </Popconfirm>
                        </Tooltip>
                    ),
                }
              ]}
              dataSource={pagos}
              rowKey="Id_Unico2"
              size="small"
              pagination={false}
            />
          </Panel>
        </Collapse>
      </div>
    );
  };

  const onExpand = async (expanded, record) => {
    if (expanded) {
      // Cargar detalles y pagos si no están cargados
    //   console.log(" Expandiendo !!! ");
    //   console.log(detallesExpandidos[record.NFactura]);
        // console.log(pagosExpandidos[record.NFactura]);      
      
      
      if (!detallesExpandidos[record.NFactura]) {
        const detalles = await fetchDetallesFactura(record.NFactura,record.Ejercicio);

        // console.log(" Detalles => ",detalles);
                
        setDetallesExpandidos(prev => ({
          ...prev,
          [record.NFactura]: detalles
        }));
        
      }

      if (!pagosExpandidos[record.NFactura]) {
        const pagos = await fetchPagosFactura(record.NFactura,record.Ejercicio);

        // console.log(" Pagos => ",pagos);
        
        setPagosExpandidos(prev => ({
          ...prev,
          [record.NFactura]: pagos
        }));
      }      
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedRows(selectedRows);
    },
  };

    // Componente de filtros
    const filtrosBar = (
        <Card 
            size="small" 
            title={
                <Space>
                    <FilterOutlined />
                    <span>Filtros de Búsqueda</span>
                </Space>
            }
            style={{ marginBottom: 16 }}
            extra={
                <Space>
                    <Button 
                        type="primary" 
                        icon={<SearchOutlined />} 
                        onClick={handleFiltrar}
                        loading={loading}
                    >
                        Buscar
                    </Button>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={handleLimpiarFiltros}
                    >
                        Limpiar
                    </Button>
                </Space>
            }
        >
        <Row gutter={[16, 16]}>
            {/* Añadir este combo de ejercicio */}
            <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                    <Text strong>Ejercicio:</Text>
                </div>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Seleccione ejercicio"
                    value={filtros.ejercicio}
                    onChange={(value) => setFiltros({ ...filtros, ejercicio: value })}
                    allowClear={false} // No permitir limpiar, siempre debe haber uno seleccionado
                    loading={ejercicios.length === 0}
                >
                    {ejercicios.map(ej => (
                        <Option key={ej.value} value={ej.value}>
                            {ej.label}
                        </Option>
                    ))}
                </Select>
            </Col>      
            <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                    <Text strong>Fecha:</Text>
                </div>
                <RangePicker
                    style={{ width: '100%' }}
                    value={[filtros.fechaDesde, filtros.fechaHasta]}
                    onChange={(dates) => setFiltros({
                        ...filtros,
                        fechaDesde: dates?.[0] || null,
                        fechaHasta: dates?.[1] || null
                    })}
                    format="DD/MM/YYYY"
                />
            </Col>
      
            {/* Resto de filtros... */}
            <Col span={8}>
                <div style={{ marginBottom: 8 }}>
                    <Text strong>Agencia:</Text>
                </div>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Seleccione agencia"
                    value={filtros.agencia}
                    onChange={(value) => setFiltros({ ...filtros, agencia: value })}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {agencias.map(agencia => (
                        <Option key={agencia.codigo} value={agencia.codigo}>
                            {agencia.codigo} - {agencia.nombre}
                        </Option>
                    ))}
                </Select>
            </Col>
      
<Col span={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>N° Factura:</Text>
          </div>
          <Input
            placeholder="Buscar por número"
            value={filtros.nfactura}
            onChange={(e) => setFiltros({ ...filtros, nfactura: e.target.value })}
            allowClear
          />
        </Col>
        
        <Col span={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Localizador:</Text>
          </div>
          <Input
            placeholder="Buscar por localizador"
            value={filtros.snumero}
            onChange={(e) => setFiltros({ ...filtros, snumero: e.target.value })}
            allowClear
          />
        </Col>
        
        <Col span={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Contabilizada:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Estado contabilización"
            value={filtros.contabilizada}
            onChange={(value) => setFiltros({ ...filtros, contabilizada: value })}
            allowClear
          >
            <Option value="S">Sí</Option>
            <Option value="N">No</Option>
          </Select>
        </Col>
        
        <Col span={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Cobrada:</Text>
          </div>
          <Select
            style={{ width: '100%' }}
            placeholder="Estado cobro"
            value={filtros.cobrada}
            onChange={(value) => setFiltros({ ...filtros, cobrada: value })}
            allowClear
          >
            <Option value="S">Completamente</Option>
            <Option value="P">Parcialmente</Option>
            <Option value="N">Pendiente</Option>
          </Select>
        </Col>
      
    </Row>
  </Card>
);

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Facturas" 
          value={facturas.length}
          prefix={<FileTextOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Total Importe" 
          value={facturas.reduce((sum, f) => sum + parseFloat(f.Total || 0), 0).toFixed(2)}
          suffix="€"
          prefix={<EuroOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Pendiente Cobro" 
          value={facturas.reduce((sum, f) => {
            const total = parseFloat(f.Total || 0);
            const pagado = parseFloat(f.TPagado || 0);
            return sum + (total - pagado);
          }, 0).toFixed(2)}
          suffix="€"
          prefix={<DollarOutlined />}
          valueStyle={{ color: '#ff4d4f' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Seleccionadas" 
          value={selectedRows.length}
          suffix={`/ ${facturas.length}`}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Facturas"
        subtitle={`${facturas.length} facturas encontradas`}
        icon={<FileTextOutlined />}
        loading={loading}
        onRefresh={() => fetchFacturas()}
        badgeCount={facturas.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              onClick={handleImprimirResumen}
              disabled={selectedRows.length === 0}
            >
              Imprimir Selección
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              onClick={handleImprimirTodas}
              disabled={facturas.length === 0}
            >
              Imprimir Todas
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportar}
            >
              Exportar
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Filtros */}
        {filtrosBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={facturas}
          rowKey="NFactura"
          loading={loading}
          scroll={{ x: 1500, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          rowSelection={rowSelection}
          expandable={{
            expandedRowRender,
            onExpand,
            rowExpandable: record => record.expandible,
          }}
            locale={{
            emptyText: filtros.fechaDesde && filtros.fechaHasta && filtros.ejercicio 
                ? 'No se encontraron facturas con los filtros aplicados' 
                : 'Seleccione filtros y haga clic en "Buscar" para ver facturas'
            }}
        />
      </PageContainer>

      {/* Modal de cobro */}
      <Modal
        title={`Cobrar Factura ${facturaSeleccionada?.Serie || 'A'}/${facturaSeleccionada?.NFactura}`}
        open={modalCobroVisible}
        onOk={handleCobroSubmit}
        onCancel={() => {
          setModalCobroVisible(false);
          formCobro.resetFields();
        }}
        width={500}
        okText="Registrar Pago"
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        {facturaSeleccionada && (
          <div style={{ marginBottom: 20 }}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Total Factura">
                <strong>{parseFloat(facturaSeleccionada.Total || 0).toFixed(2)} €</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Ya Pagado">
                {parseFloat(facturaSeleccionada.TPagado || 0).toFixed(2)} €
              </Descriptions.Item>
              <Descriptions.Item label="Pendiente">
                <strong style={{ color: '#ff4d4f' }}>
                  {(parseFloat(facturaSeleccionada.Total || 0) - parseFloat(facturaSeleccionada.TPagado || 0)).toFixed(2)} €
                </strong>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
        
        <Form
          form={formCobro}
          layout="vertical"
        >
          <Form.Item
            name="fecha"
            label="Fecha de Pago"
            rules={[{ required: true, message: 'Seleccione la fecha' }]}
          >
            <DatePicker 
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
            />
          </Form.Item>
          
          <Form.Item
            name="formaPago"
            label="Forma de Pago"
            rules={[{ required: true, message: 'Seleccione forma de pago' }]}
          >
            <Select placeholder="Seleccione forma de pago">
              {formasPago
                .filter(fp => fp.mostrar !== false)
                .map(fp => (
                  <Option key={fp.codigo} value={fp.codigo}>
                    {fp.codigo} - {fp.descripcion}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="importe"
            label="Importe a Pagar"
            rules={[
              { required: true, message: 'Ingrese el importe' },
              {
                validator: (_, value) => {
                  const max = facturaSeleccionada 
                    ? parseFloat(facturaSeleccionada.Total) - parseFloat(facturaSeleccionada.TPagado || 0)
                    : 0;
                  
                  if (value > max) {
                    return Promise.reject(new Error(`El importe no puede ser mayor a ${max.toFixed(2)} €`));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={facturaSeleccionada 
                ? parseFloat(facturaSeleccionada.Total) - parseFloat(facturaSeleccionada.TPagado || 0)
                : 0
              }
              step={0.01}
              precision={2}
              addonAfter="€"
            />
          </Form.Item>
          
          <Form.Item
            name="observaciones"
            label="Observaciones"
          >
            <TextArea
              placeholder="Observaciones adicionales..."
              rows={2}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de detalles */}
      <Modal
        title={`Detalles Factura ${facturaDetalles?.Serie || 'A'}/${facturaDetalles?.NFactura}`}
        open={modalDetallesVisible}
        onCancel={() => setModalDetallesVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setModalDetallesVisible(false)}>
            Cerrar
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => handleImprimir(facturaDetalles)}
          >
            Imprimir Factura
          </Button>
        ]}
      >
        {facturaDetalles && (
          <div>
            {/* Información de la factura */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions title="Información de la Factura" column={2} bordered size="small">
                <Descriptions.Item label="Número">{facturaDetalles.Serie || 'A'}/{facturaDetalles.NFactura}</Descriptions.Item>
                <Descriptions.Item label="Ejercicio">{facturaDetalles.Ejercicio}</Descriptions.Item>
                <Descriptions.Item label="Fecha">{dayjs(facturaDetalles.Fecha).format('DD/MM/YYYY')}</Descriptions.Item>
                <Descriptions.Item label="Reserva">{facturaDetalles.NReserva}</Descriptions.Item>
                <Descriptions.Item label="Agencia">{facturaDetalles.Agencia} - {facturaDetalles.NomAgencia}</Descriptions.Item>
                <Descriptions.Item label="Localizador">{facturaDetalles.SNumero}</Descriptions.Item>
                <Descriptions.Item label="Contabilizada">
                  <Tag color={facturaDetalles.Contabilizado === 'S' ? 'green' : 'red'}>
                    {facturaDetalles.Contabilizado === 'S' ? 'Sí' : 'No'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Cobrada">
                  <Tag color={facturaDetalles.Cobrado === 'S' ? 'green' : facturaDetalles.TPagado > 0 ? 'orange' : 'red'}>
                    {facturaDetalles.Cobrado === 'S' ? 'Completo' : facturaDetalles.TPagado > 0 ? 'Parcial' : 'Pendiente'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  <strong>{parseFloat(facturaDetalles.Total || 0).toFixed(2)} €</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Pagado">
                  {parseFloat(facturaDetalles.TPagado || 0).toFixed(2)} €
                </Descriptions.Item>
                <Descriptions.Item label="Base Imponible">
                  {parseFloat(facturaDetalles.BaseImponible || 0).toFixed(2)} €
                </Descriptions.Item>
                <Descriptions.Item label="IVA">
                  {parseFloat(facturaDetalles.ImporteIVA || 0).toFixed(2)} €
                </Descriptions.Item>
              </Descriptions>
            </Card>
            
            {/* Detalles de la factura */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Title level={5}>Detalles de Líneas</Title>
              <Table
                columns={[
                  { title: 'Habitación', dataIndex: 'Habitacion', key: 'Habitacion' },
                  { title: 'Cliente', dataIndex: 'Cliente', key: 'Cliente' },
                  { title: 'Entrada', dataIndex: 'FechaEnt', key: 'FechaEnt',
                    render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                  { title: 'Salida', dataIndex: 'FechaSal', key: 'FechaSal',
                    render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                  { title: 'Pax', dataIndex: 'Nagrupa', key: 'Nagrupa', width: 80 },
                  { title: 'Días', dataIndex: 'DiasCant', key: 'DiasCant' },
                  { title: 'Precio', dataIndex: 'Precio', key: 'Precio',
                    render: precio => `${parseFloat(precio || 0).toFixed(2)} €` },
                  { title: 'Base', dataIndex: 'ImporteBase', key: 'ImporteBase',
                    render: base => `${parseFloat(base || 0).toFixed(2)} €` },
                  { title: 'IVA', dataIndex: 'ImporteIVA', key: 'ImporteIVA',
                    render: iva => `${parseFloat(iva || 0).toFixed(2)} €` },
                ]}
                dataSource={detallesFactura}
                rowKey="i"
                size="small"
                pagination={false}
              />
            </Card>
            
            {/* Pagos */}
            {pagosFactura.length > 0 && (
              <Card size="small">
                <Title level={5}>Pagos Registrados</Title>
                <Table
                  columns={[
                    { title: 'Fecha', dataIndex: 'Fecha', key: 'Fecha',
                      render: fecha => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-' },
                    { title: 'Forma Pago', dataIndex: 'FPago', key: 'FPago' },
                    { title: 'Importe', dataIndex: 'Pagado', key: 'Pagado',
                      render: importe => `${parseFloat(importe || 0).toFixed(2)} €` },
                    { title: 'Usuario', dataIndex: 'usuario', key: 'usuario' },
                    { title: 'Contabilizado', dataIndex: 'Contabilizado', key: 'Contabilizado',
                      render: contabilizado => contabilizado ? 'Sí' : 'No' },
                    {
                            title: 'Acciones',
                            key: 'acciones',
                            width: 120,
                            render: (_, pago) => (
                                <Tooltip title="Eliminar cobro">
                                <Popconfirm
                                    title="¿Eliminar este cobro?"
                                    description="Esta acción eliminará el registro de pago"
                                    onConfirm={() => handleEliminarCobro(facturaDetalles, pago)}
                                    okText="Sí"
                                    cancelText="No"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    size="small"
                                    disabled={pago.Contabilizado}
                                    />
                                </Popconfirm>
                                </Tooltip>
                            ),
                            },                      
                  ]}
                  dataSource={pagosFactura}
                  rowKey="Id_Unico2"
                  size="small"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

// Componente Progress para estado de pago
const Progress = ({ percent, size = 'default', status = 'normal' }) => {
  const colors = {
    success: '#52c41a',
    exception: '#ff4d4f',
    active: '#1890ff',
    normal: '#1890ff'
  };
  
  return (
    <div style={{ 
      width: '100%', 
      backgroundColor: '#f5f5f5', 
      borderRadius: 4,
      overflow: 'hidden',
      height: size === 'small' ? 6 : 8
    }}>
      <div style={{
        width: `${Math.min(percent, 100)}%`,
        backgroundColor: colors[status],
        height: '100%',
        transition: 'width 0.3s'
      }} />
    </div>
  );
};

export default Facturas;